import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router';
import type { AuthPayload, AuthStatus } from './createAuthStore';

interface AuthBootstrapStore<TPayload extends AuthPayload> {
  status: AuthStatus;
  setChecking: () => void;
  setAnonymous: () => void;
  setAuthenticated: (payload: TPayload) => void;
}

export interface PlatformAuthBootstrapProps<TMe, TPayload extends AuthPayload> {
  fetchMe: () => Promise<TMe>;
  isAuthenticated: (me: TMe) => boolean;
  mapAuthenticated: (me: TMe) => TPayload;
  useAuthStore: <T>(selector: (state: AuthBootstrapStore<TPayload>) => T) => T;
  loadingText?: string;
}

/** Loads `/api/auth/me` once — Entra cookie session via each app's BFF. */
export function PlatformAuthBootstrap<TMe, TPayload extends AuthPayload>({
  fetchMe,
  isAuthenticated,
  mapAuthenticated,
  useAuthStore,
  loadingText = 'Loading...'
}: PlatformAuthBootstrapProps<TMe, TPayload>) {
  const setChecking = useAuthStore((s) => s.setChecking);
  const setAnonymous = useAuthStore((s) => s.setAnonymous);
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const status = useAuthStore((s) => s.status);

  // Keep latest callbacks without re-running bootstrap when parents pass inline lambdas.
  const fetchMeRef = useRef(fetchMe);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const mapAuthenticatedRef = useRef(mapAuthenticated);
  fetchMeRef.current = fetchMe;
  isAuthenticatedRef.current = isAuthenticated;
  mapAuthenticatedRef.current = mapAuthenticated;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setChecking();
      try {
        const me = await fetchMeRef.current();
        if (cancelled) return;
        if (!isAuthenticatedRef.current(me)) {
          setAnonymous();
          return;
        }
        setAuthenticated(mapAuthenticatedRef.current(me));
      } catch {
        if (!cancelled) setAnonymous();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setAnonymous, setAuthenticated, setChecking]);

  if (status === 'checking') {
    return (
      <div className="auth-bootstrap-loading">
        <div className="auth-bootstrap-spinner" aria-hidden />
        <p className="auth-bootstrap-text">{loadingText}</p>
      </div>
    );
  }

  return <Outlet />;
}
